import { Color as ThreeColor, MeshStandardMaterial } from 'three';
import type { Color, TileType } from '../core/types.js';
import { LOCKED_COLOR, PENALTY_COLOR } from '../core/palette.js';

const cache = new Map<string, MeshStandardMaterial>();

const make = (hex: string, opts: { metalness: number; roughness: number; emissive: number }) => {
  return new MeshStandardMaterial({
    color: new ThreeColor(hex),
    metalness: opts.metalness,
    roughness: opts.roughness,
    emissive: new ThreeColor(hex),
    emissiveIntensity: opts.emissive,
  });
};

export const materialFor = (color: Color, tile: TileType): MeshStandardMaterial => {
  const key = `${color}|${tile}`;
  const cached = cache.get(key);
  if (cached) return cached;
  let m: MeshStandardMaterial;
  if (tile === 'locked')
    m = make(LOCKED_COLOR, { metalness: 0.55, roughness: 0.35, emissive: 0.04 });
  else if (tile === 'penalty')
    m = make(PENALTY_COLOR, { metalness: 0.4, roughness: 0.55, emissive: 0.06 });
  else if (tile === 'inversion' || tile === 'chaos')
    m = make(color, { metalness: 0.65, roughness: 0.18, emissive: 0.32 });
  else if (tile === 'rotateL' || tile === 'rotateR' || tile === 'mirrorH' || tile === 'mirrorV')
    m = make(color, { metalness: 0.45, roughness: 0.22, emissive: 0.26 });
  else m = make(color, { metalness: 0.18, roughness: 0.42, emissive: 0.18 });
  cache.set(key, m);
  return m;
};

export const floorMaterial = (): MeshStandardMaterial => {
  return new MeshStandardMaterial({
    color: new ThreeColor('#eef0f7'),
    metalness: 0.05,
    roughness: 0.8,
  });
};

export const haloMaterial = (color: string): MeshStandardMaterial => {
  return new MeshStandardMaterial({
    color: new ThreeColor(color),
    metalness: 0,
    roughness: 1,
    emissive: new ThreeColor(color),
    emissiveIntensity: 0.9,
    transparent: true,
    opacity: 0.95,
  });
};

export const disposeMaterials = (): void => {
  cache.forEach((m) => m.dispose());
  cache.clear();
};
